import React, { FC, useCallback, useMemo, useState } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';
import Popover, { PopoverPlacement } from 'react-native-popover-view';
import { makeStyles } from './SystemStatus.styles';
import { useTheme } from '../../hooks/useTheme';
import SystemStatusRow, { STATES, SystemStatusAction } from './SystemStatusRow';
import { useTranslation } from '@oolio-group/localization';
import Icon from '../Icon/Icon';
import { useModal } from '@oolio-group/rn-use-modal';
import NetworkDevicesModal from '../Modals/NetworkDevicesModal/NetworkDevicesModal';
import ScreenSelectionModal from '../Modals/ScreenSelectionModal/ScreenSelectionModal';
import AndroidScreenSelectionModal from '../Modals/AndroidScreenSelectionModal/AndroidScreenSelectionModal';
import { useLogout } from '../../hooks/device/useLogout';
import useBehaviorSubjectState from '../../hooks/rxjs/useSubjectState';
import { localCommsSubject } from '../../state/localComms';
import { localComms } from '../../utils/localComms';
import { isDesktop } from '../../utils/environment';

const SystemStatus: FC = () => {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const { translate } = useTranslation();
  const { showModal, closeModal } = useModal();
  const { logout } = useLogout();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { value: isLocalCommsEligible } =
    useBehaviorSubjectState(localCommsSubject);

  const onLogout = useCallback(() => {
    setShowOptions(false);
    logout();
  }, [logout]);

  const onToggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const showDevicesPopup = useCallback(() => {
    closeModal();
    setShowOptions(false);
    setTimeout(() => {
      showModal(<NetworkDevicesModal onClose={closeModal} />);
    }, 1000);
  }, [showModal, closeModal]);

  const showScreenSelectionModal = useCallback(() => {
    closeModal();
    setShowOptions(false);
    setTimeout(() => {
      showModal(<ScreenSelectionModal onClose={closeModal} />);
    }, 1000);
  }, [showModal, closeModal]);

  const showAndroidScreenSelectionModal = useCallback(() => {
    closeModal();
    setShowOptions(false);
    setTimeout(() => {
      showModal(<AndroidScreenSelectionModal onClose={closeModal} />);
    }, 1000);
  }, [showModal, closeModal]);

  const onPressAction = useCallback((name: string, action?: () => void) => {
    action && action();
  }, []);

  const OPTIONS: SystemStatusAction[] = useMemo(() => {
    let defaultOptions = [
      {
        testID: 'btn-logout',
        label: translate('button.logout'),
        type: STATES.NEGATIVE,
        action: onLogout,
      },
    ];

    const additionalOptions = [];

    if (isLocalCommsEligible) {
      additionalOptions.push({
        testID: 'status-devices',
        label: translate('backOfficeSettings.devices'),
        type: STATES.POSITIVE,
        action: showDevicesPopup,
      });
    }

    if (isDesktop() && localComms.toggleFullScreen) {
      additionalOptions.push({
        testID: 'btn-fullscreen',
        label: translate('settings.toggleFullscreen'),
        type: STATES.NEUTRAL,
        action: async () => {
          setShowOptions(false);
          try {
            await localComms.toggleFullScreen?.();
          } catch (error) {
            console.error('Failed to toggle fullscreen:', error);
          }
        },
      });
    }

    if (isDesktop() && localComms.getAvailableScreens) {
      additionalOptions.push({
        testID: 'btn-select-screen',
        label: translate('settings.selectScreen'),
        type: STATES.NEUTRAL,
        action: showScreenSelectionModal,
      });
    }

    if (Platform.OS === 'android') {
      additionalOptions.push({
        testID: 'btn-select-screen-android',
        label: translate('settings.selectScreen'),
        type: STATES.NEUTRAL,
        action: showAndroidScreenSelectionModal,
      });
    }

    if (additionalOptions.length > 0) {
      defaultOptions = [...additionalOptions, ...defaultOptions];
    }

    return defaultOptions;
  }, [
    onLogout,
    showDevicesPopup,
    showScreenSelectionModal,
    showAndroidScreenSelectionModal,
    isLocalCommsEligible,
    translate,
  ]);

  return (
    <View>
      <Popover
        from={
          <TouchableOpacity
            testID="btn-system"
            onPress={onToggleOptions}
            style={[
              styles.button,
              {
                backgroundColor: colors.elevation0,
              },
            ]}
          >
            <Icon name="UilSetting" size={20} color={colors.text} />
          </TouchableOpacity>
        }
        isVisible={showOptions}
        placement={PopoverPlacement.AUTO}
        onRequestClose={() => setShowOptions(false)}
        backgroundStyle={styles.bgStyle}
        popoverStyle={styles.optionsContainer}
      >
        <View style={styles.optionsBg}>
          {OPTIONS.map((option, i) => (
            <SystemStatusRow
              key={option.testID}
              testID={option.testID}
              type={option.type}
              label={option.label}
              action={() => {
                onPressAction(option.label, option.action);
              }}
            />
          ))}
        </View>
      </Popover>
    </View>
  );
};

export default SystemStatus;
